import React, { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./RequestDemo.css";
import { Context } from "../../../context/Context";
import emailjs from "@emailjs/browser";
import { translations } from "../../../utils/lang";
import { addcrmAPI } from "../../../utils/api/crm";
import Logo from "../../../assets/logo/logo.svg";

const baseRequest = {
  name: "",
  email: "",
  phone: "",
  city: "",
  businessName: "",
  branchNumber: "",
};

const successText = {
  ar: {
    title: "تم استلام طلبك بنجاح",
    desc1: "تحقّق من بريدك الإلكتروني واتبع الرابط في الرسالة لتعيين كلمة المرور.",
    desc2:
      "إذا لم تصلك الرسالة، يرجى التحقق من مجلد البريد غير المرغوب فيه (Spam).",
  },
  en: {
    title: "Your request has been received successfully",
    desc1: "Please check your email and follow the link in the message to set your password.",
    desc2:
      "If you do not receive the email, please check your Spam or Junk folder.",
  },
};

// One random id per form, kept until the registration succeeds: a retry (double click, network drop) repeats the SAME id, so the server
// returns the same result instead of creating a second store or account.
const newRegistrationId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, "");
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  }
};

export const RequestDemo = () => {
  const { lang } = useContext(Context);
  const tSuccess = successText[lang] || successText.en;
  const tForm = translations[lang]?.requestDemo || translations.en.requestDemo;
  const tAuth = translations[lang]?.signinup || translations.en.signinup;

  const [formData, setFormData] = useState(baseRequest);
  const [formErrors, setFormErrors] = useState({
    name: false,
    email: false,
    phone: false,
    city: false,
    businessName: false,
    branchNumber: false,
    somethingWrong: false,
    alreadyRegistered: false,
  });
  const [isSent, setIsSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const registrationIdRef = useRef(null);

  const changeHandler = (e) => {
    let { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (value.trim() !== "") {
      setFormErrors({ ...formErrors, [name]: false });
    }
  };

  const validateForm = () => {
    const emailregex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneregex = /^\d{9}$/;
    const errors = {
      name: formData.name.trim() === "",
      email: !emailregex.test(formData.email),
      phone: !phoneregex.test(formData.phone),
      city: formData.city.trim() === "",
      businessName: formData.businessName.trim() === "",
      branchNumber: formData.branchNumber.trim() === "",
      somethingWrong: false,
      alreadyRegistered: false,
    };
    setFormErrors(errors);
    return !Object.values(errors).includes(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSending || !validateForm()) return;
    setIsSending(true);

    const data = formData;
    if (!registrationIdRef.current) registrationIdRef.current = newRegistrationId();

    // The registration comes FIRST: the account, store and lead are created (or an identical earlier attempt is recognised) before
    // anything else happens, and the team notification is sent only for a registration that really is new.
    const result = await addcrmAPI({ ...data, registrationId: registrationIdRef.current });

    if (!result.ok) {
      // a refusal is shown as such (and the team is not notified); the same id is kept so a retry stays idempotent
      const refused = result.status === 409 && result.code !== "IDENTITY_CONFLICT";
      setFormErrors({ somethingWrong: !refused, alreadyRegistered: refused });
      setIsSending(false);
      return;
    }

    if (!result.replayed) {
      try {
        await emailjs.send("service_4rjdkov", "template_c3g2hnt", data, "FfTOgE4Ku2D579iBZ");
      } catch (error) {
        // the registration succeeded; only the internal notification failed
        console.error("EmailJS error:", error);
      }
    }
    registrationIdRef.current = null;
    setIsSent(true);
    setFormData(baseRequest);
    setIsSending(false);
  };

  return (
    <div className="contact-page">
      {isSent ? (
        <div className="contact-success">
          <div className="contact-success-card">
            <div className="contact-success-hero" aria-hidden="true">
              <span className="success-star star-1"></span>
              <span className="success-star star-2"></span>
              <span className="success-star star-3"></span>
              <span className="success-star star-4"></span>

              <div className="success-rocket">
                <div className="rocket-window"></div>
                <div className="rocket-body"></div>
                <div className="rocket-left-wing"></div>
                <div className="rocket-right-wing"></div>
                <div className="rocket-bottom"></div>
                <div className="rocket-fire"></div>
              </div>

              <div className="success-clouds"></div>
            </div>

            <div className="contact-success-content">
              <h2 className="request-demo-success-title">{tSuccess.title}</h2>

              <p className="request-demo-success-p">{tSuccess.desc1}</p>

              <p className="request-demo-success-note">{tSuccess.desc2}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="contact-card">
          <form onSubmit={handleSubmit} className="contact-form-panel">
            <div className="contact-form-head">
              <img src={Logo} alt="LUD" className="contact-form-logo" />
              <div>
                <h2>{tForm.title}</h2>
              </div>
            </div>

            <div className="contact-form-grid">
              <div
                className={`contact-field ${formErrors.name ? "error" : ""}`}
              >
                <label>{tForm.name}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={changeHandler}
                />
                {formErrors.name && (
                  <p className="contact-error-text">{tForm.nameError}</p>
                )}
              </div>

              <div
                className={`contact-field ${formErrors.email ? "error" : ""}`}
              >
                <label>{tForm.email}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={changeHandler}
                />
                {formErrors.email && (
                  <p className="contact-error-text">{tForm.emailError}</p>
                )}
              </div>

              <div
                className={`contact-field ${formErrors.city ? "error" : ""}`}
              >
                <label>{tForm.city}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={changeHandler}
                />
                {formErrors.city && (
                  <p className="contact-error-text">{tForm.cityError}</p>
                )}
              </div>

              <div
                className={`contact-field ${formErrors.phone ? "error" : ""}`}
              >
                <label>{tForm.phoneNumber}</label>
                <div className="contact-field-phone">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={changeHandler}
                  />
                  <div className="contact-field-code">+966</div>
                </div>
                {formErrors.phone && (
                  <p className="contact-error-text">{tForm.phoneError}</p>
                )}
              </div>

              <div
                className={`contact-field ${
                  formErrors.businessName ? "error" : ""
                }`}
              >
                <label>{tForm.businessName}</label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={changeHandler}
                />
                {formErrors.businessName && (
                  <p className="contact-error-text">
                    {tForm.businessNameError}
                  </p>
                )}
              </div>

              <div
                className={`contact-field ${
                  formErrors.branchNumber ? "error" : ""
                }`}
              >
                <label>{tForm.branchNumber}</label>
                <input
                  type="number"
                  name="branchNumber"
                  value={formData.branchNumber}
                  onChange={changeHandler}
                />
                {formErrors.branchNumber && (
                  <p className="contact-error-text">
                    {tForm.branchNumberError}
                  </p>
                )}
              </div>
            </div>

            {formErrors.somethingWrong && (
              <p className="contact-error-text contact-error-general">
                {tForm.somethingWrong}
              </p>
            )}
            {formErrors.alreadyRegistered && (
              <p className="contact-error-text contact-error-general" role="alert">
                {tForm.alreadyRegistered}
              </p>
            )}

            <button
              type="submit"
              className="contact-submit"
              disabled={isSending}
            >
              {isSending ? tForm.sending : tForm.submit}
            </button>
          </form>

          <div className="contact-info-panel">
            <div>
              <div className="cta-label">
                {lang === "ar" ? "تواصل معنا" : "Contact Us"}
              </div>

              <h2 className="cta-title">
                {lang === "ar" ? (
                  <>
                    جاهز لتحويل <span>توصيلك؟</span>
                  </>
                ) : (
                  <>
                    Ready to Transform <span>Your Delivery?</span>
                  </>
                )}
              </h2>

              <p className="cta-desc">
                {lang === "ar"
                  ? "بعد تعبئة البيانات وإرسال الطلب، سيصلك بريد إلكتروني يحتوي على رابط تسجيل الدخول لاستكمال بيانات الحساب وإتمام إعداد حسابك."
                  : "After submitting your details, you will receive an email with a sign-in link to complete your account information and finish setting up your account."}
              </p>
            </div>

            <Link to="/signin" className="contact-signin-link">
              {tAuth.signupQuestion}
              <div className="hero-link-cta">{tAuth.signinhere}</div>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
